import React, { useState, useEffect } from 'react';
import { useInput, useDataProvider, useTranslate } from 'react-admin';
import {
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    Chip,
    Alert,
    CircularProgress,
} from '@mui/material';
import {
    ElectricBolt,
    Bolt,
    DirectionsCar,
    KeyboardArrowRight,
} from '@mui/icons-material';
import { FeeEntityType, EntitySelection } from '../../../types/fee.types';

interface EntitySelectorProps {
    source: string;
    showHierarchy?: boolean;
    disabled?: boolean;
}

interface EntityOption {
    id: string;
    name: string;
    siteId?: string;
    loadGroupId?: string;
}

export const EntitySelector: React.FC<EntitySelectorProps> = ({ 
    source, 
    showHierarchy = true,
    disabled = false 
}) => {
    const translate = useTranslate();
    const dataProvider = useDataProvider();
    const {
        field: { value = { type: 'site', id: '', name: '' }, onChange },
        fieldState: { error },
    } = useInput({ source });

    const [loading, setLoading] = useState(false);
    const [sites, setSites] = useState<EntityOption[]>([]);
    const [loadGroups, setLoadGroups] = useState<EntityOption[]>([]);
    const [chargers, setChargers] = useState<EntityOption[]>([]);
    const [selectedSite, setSelectedSite] = useState<string>('');
    const [selectedLoadGroup, setSelectedLoadGroup] = useState<string>('');

    // Load sites on component mount
    useEffect(() => {
        loadSites();
    }, []);

    // Load load groups when site changes
    useEffect(() => {
        if (selectedSite) {
            loadLoadGroups(selectedSite);
        } else {
            setLoadGroups([]);
            setChargers([]);
        }
    }, [selectedSite]);

    // Load chargers when load group changes
    useEffect(() => {
        if (selectedLoadGroup) {
            loadChargers(selectedLoadGroup);
        } else if (selectedSite && !selectedLoadGroup) {
            // Load all chargers for the site
            loadChargers(selectedSite, 'site');
        } else {
            setChargers([]);
        }
    }, [selectedLoadGroup, selectedSite]);

    // Set hierarchy when value changes externally
    useEffect(() => {
        if (value.id && value.type) {
            setHierarchyFromValue(value);
        }
    }, [value.id, value.type]);

    const loadSites = async () => {
        setLoading(true);
        try {
            const { data } = await dataProvider.getList('sites', {
                pagination: { page: 1, perPage: 1000 },
                sort: { field: 'name', order: 'ASC' },
                filter: {},
            });
            setSites(data);
        } catch (error) {
            console.error('Failed to load sites:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadLoadGroups = async (siteId: string) => {
        setLoading(true);
        try {
            const { data } = await dataProvider.getList('load-groups', {
                pagination: { page: 1, perPage: 1000 },
                sort: { field: 'name', order: 'ASC' },
                filter: { siteId },
            });
            setLoadGroups(data);
        } catch (error) {
            console.error('Failed to load load groups:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadChargers = async (parentId: string, parentType: 'site' | 'load_group' = 'load_group') => {
        setLoading(true);
        try {
            const filter = parentType === 'site' 
                ? { siteId: parentId }
                : { loadGroupId: parentId };
            
            const { data } = await dataProvider.getList('chargers', {
                pagination: { page: 1, perPage: 1000 },
                sort: { field: 'name', order: 'ASC' },
                filter,
            });
            setChargers(data);
        } catch (error) {
            console.error('Failed to load chargers:', error);
        } finally {
            setLoading(false);
        }
    };

    const setHierarchyFromValue = async (selection: EntitySelection) => {
        if (selection.type === 'site') {
            setSelectedSite(selection.id);
            setSelectedLoadGroup('');
        } else if (selection.type === 'load_group') {
            // Find the site for this load group
            try {
                const { data: loadGroup } = await dataProvider.getOne('load-groups', { id: selection.id });
                if (loadGroup.siteId) {
                    setSelectedSite(loadGroup.siteId);
                    setSelectedLoadGroup(selection.id);
                }
            } catch (error) {
                console.error('Failed to load load group details:', error);
            }
        } else if (selection.type === 'charger') {
            // Find the site and load group for this charger
            try {
                const { data: charger } = await dataProvider.getOne('chargers', { id: selection.id });
                if (charger.siteId) {
                    setSelectedSite(charger.siteId);
                    if (charger.loadGroupId) {
                        setSelectedLoadGroup(charger.loadGroupId);
                    }
                }
            } catch (error) {
                console.error('Failed to load charger details:', error);
            }
        }
    };

    const handleEntityTypeChange = (newType: FeeEntityType) => {
        // When changing entity type, set the appropriate entity id based on current selection
        let entityId = '';
        let entityName = '';
        
        if (newType === 'site' && selectedSite) {
            entityId = selectedSite;
            entityName = sites.find(s => s.id === selectedSite)?.name || '';
        } else if (newType === 'load_group' && selectedLoadGroup) {
            entityId = selectedLoadGroup;
            entityName = loadGroups.find(lg => lg.id === selectedLoadGroup)?.name || '';
        } else if (newType === 'charger' && chargers.length > 0) {
            // When switching to charger, don't auto-select
            entityId = '';
            entityName = '';
        }
        
        const newValue = { type: newType, id: entityId, name: entityName };
        onChange(newValue);

        // Reset hierarchy
        if (newType === 'site') {
            setSelectedLoadGroup('');
        }
    };

    const handleEntityChange = (entityId: string, entityType: FeeEntityType) => {
        let entityName = '';
        
        // Find entity name
        if (entityType === 'site') {
            entityName = sites.find(s => s.id === entityId)?.name || '';
            setSelectedSite(entityId);
            setSelectedLoadGroup('');
        } else if (entityType === 'load_group') {
            entityName = loadGroups.find(lg => lg.id === entityId)?.name || '';
            setSelectedLoadGroup(entityId);
        } else if (entityType === 'charger') {
            entityName = chargers.find(c => c.id === entityId)?.name || '';
        }
        
        const newValue = { type: entityType, id: entityId, name: entityName };
        onChange(newValue);
    };

    const entityIcons: Record<FeeEntityType, React.ReactElement> = {
        site: <ElectricBolt />,
        load_group: <Bolt />,
        charger: <DirectionsCar />,
    };

    const entityLabels: Record<FeeEntityType, string> = {
        site: translate('resources.fee-schedules.fields.site'),
        load_group: translate('resources.fee-schedules.fields.loadGroup'),
        charger: translate('resources.fee-schedules.fields.charger'),
    };

    const getAvailableEntities = () => {
        switch (value.type) {
            case 'site':
                return sites;
            case 'load_group':
                return loadGroups;
            case 'charger':
                return chargers;
            default:
                return [];
        }
    };

    return (
        <Box sx={{ width: '100%' }}>
            {/* Entity Type Selection */}
            <FormControl fullWidth sx={{ mb: 2 }} disabled={disabled}>
                <InputLabel>{translate('resources.fee-schedules.fields.entityType')}</InputLabel>
                <Select
                    value={value.type}
                    label={translate('resources.fee-schedules.fields.entityType')}
                    onChange={(e) => handleEntityTypeChange(e.target.value as FeeEntityType)}
                >
                    <MenuItem value="site">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {entityIcons.site}
                            {entityLabels.site}
                        </Box>
                    </MenuItem>
                    <MenuItem value="load_group">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {entityIcons.load_group}
                            {entityLabels.load_group}
                        </Box>
                    </MenuItem>
                    <MenuItem value="charger">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {entityIcons.charger}
                            {entityLabels.charger}
                        </Box>
                    </MenuItem>
                </Select>
            </FormControl>

            {/* Hierarchy Display */}
            {showHierarchy && (
                <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                        {translate('resources.fee-schedules.fields.hierarchy')}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1 }, flexWrap: 'wrap' }}>
                        {/* Site */}
                        <FormControl size="small" sx={{ minWidth: 150 }} disabled={disabled}>
                            <InputLabel>{entityLabels.site}</InputLabel>
                            <Select
                                value={selectedSite}
                                label={entityLabels.site}
                                onChange={(e) => {
                                    setSelectedSite(e.target.value);
                                    if (value.type === 'site') {
                                        handleEntityChange(e.target.value, 'site');
                                    }
                                }}
                            >
                                {sites.map((site) => (
                                    <MenuItem key={site.id} value={site.id}>
                                        {site.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {selectedSite && (
                            <>
                                <KeyboardArrowRight color="action" />
                                {/* Load Group */}
                                <FormControl size="small" sx={{ minWidth: 150 }} disabled={disabled}>
                                    <InputLabel shrink>
                                        {entityLabels.load_group}
                                    </InputLabel>
                                    <Select
                                        value={selectedLoadGroup}
                                        label={entityLabels.load_group}
                                        onChange={(e) => {
                                            setSelectedLoadGroup(e.target.value);
                                            if (value.type === 'load_group') {
                                                handleEntityChange(e.target.value, 'load_group');
                                            }
                                        }}
                                        displayEmpty
                                        notched
                                    >
                                        <MenuItem value="">
                                            <em>{translate('resources.fee-schedules.fields.allLoadGroups')}</em>
                                        </MenuItem>
                                        {loadGroups.map((lg) => (
                                            <MenuItem key={lg.id} value={lg.id}>
                                                {lg.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </>
                        )}

                        {value.type === 'charger' && selectedSite && (
                            <>
                                <KeyboardArrowRight color="action" />
                                {/* Charger */}
                                <FormControl size="small" sx={{ minWidth: 150 }} disabled={disabled}>
                                    <InputLabel>{entityLabels.charger}</InputLabel>
                                    <Select
                                        value={value.id}
                                        label={entityLabels.charger}
                                        onChange={(e) => handleEntityChange(e.target.value, 'charger')}
                                    >
                                        {chargers.map((charger) => (
                                            <MenuItem key={charger.id} value={charger.id}>
                                                {charger.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </>
                        )}
                    </Box>
                </Box>
            )}

            {/* Direct Entity Selection */}
            {!showHierarchy && (
                <FormControl fullWidth disabled={disabled}>
                    <InputLabel>
                        {translate('resources.fee-schedules.fields.selectEntity', { type: entityLabels[value.type as FeeEntityType] })}
                    </InputLabel>
                    <Select
                        value={value.id}
                        label={translate('resources.fee-schedules.fields.selectEntity', { type: entityLabels[value.type as FeeEntityType] })}
                        onChange={(e) => handleEntityChange(e.target.value, value.type)}
                    >
                        {getAvailableEntities().map((entity) => (
                            <MenuItem key={entity.id} value={entity.id}>
                                {entity.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}

            {/* Current Selection Display */}
            {value.id && (
                <Box sx={{ mt: 2 }}>
                    <Chip
                        icon={entityIcons[value.type as FeeEntityType]}
                        label={`${entityLabels[value.type as FeeEntityType]}: ${value.name || value.id}`}
                        color="primary"
                        variant="outlined"
                    />
                </Box>
            )}

            {/* Loading Indicator */}
            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <CircularProgress size={24} />
                </Box>
            )}

            {/* Error Display */}
            {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                    {error.message}
                </Alert>
            )}
        </Box>
    );
};